import type { FieldWhere, WhereLeaf } from '../../../query-dsl';

/**
 * Internal helper that translates a column id using dot notation
 * (e.g. `'boundedContext.name'`) into the nested relation object of the
 * Aurora Query DSL `where` tree:
 *
 *   nestWhereCondition('boundedContext.name', { iLike: '%x%' })
 *   // → { boundedContext: { name: { iLike: '%x%' } } }
 *
 * A relation key inside `where` filters the PARENT rows via an EXISTS check
 * (the DSL successor of Sequelize's dollar-wrapped `'$assoc.col$'` keys).
 * Deep chains nest recursively: `'account.user.name'` →
 * `{ account: { user: { name: <cond> } } }`.
 *
 * In Aurora, a column id with a dot has exactly one meaning: "field of a
 * relation." This convention lets the data-table builders
 * (`buildSearchWhere`, `buildFilterWhere`) emit the right DSL shape without
 * the consumer plumbing it manually. A flat id produces the flat entry
 * `{ <id>: <cond> }`.
 */
export function nestWhereCondition(columnId: string, condition: WhereLeaf): FieldWhere {
  const segments = columnId.split('.');
  const leafField = segments[segments.length - 1];

  let node: FieldWhere = { [leafField]: condition };
  for (let i = segments.length - 2; i >= 0; i--) {
    node = { [segments[i]]: node };
  }
  return node;
}
